import { ApprovalStep } from './expense.model';
import { PHIDataType } from './audit-log.model';

export enum RiskStatus {
  draft = 'draft',
  identified = 'identified',
  assessed = 'assessed',
  mitigating = 'mitigating',
  monitoring = 'monitoring',
  resolved = 'resolved',
  realized = 'realized', // Risk occurred
}

export const RiskStatusLabels: Record<RiskStatus, string> = {
  [RiskStatus.draft]: 'Draft',
  [RiskStatus.identified]: 'Identified',
  [RiskStatus.assessed]: 'Assessed',
  [RiskStatus.mitigating]: 'Mitigating',
  [RiskStatus.monitoring]: 'Monitoring',
  [RiskStatus.resolved]: 'Resolved',
  [RiskStatus.realized]: 'Realized',
};

export enum RiskSeverity {
  low = 'low',
  medium = 'medium',
  high = 'high',
  critical = 'critical',
}

export const RiskSeverityLabels: Record<RiskSeverity, string> = {
  [RiskSeverity.low]: 'Low',
  [RiskSeverity.medium]: 'Medium',
  [RiskSeverity.high]: 'High',
  [RiskSeverity.critical]: 'Critical',
};

export enum RiskLikelihood {
  veryLow = 'very_low',
  low = 'low',
  medium = 'medium',
  high = 'high',
  veryHigh = 'very_high',
}

export const RiskLikelihoodLabels: Record<RiskLikelihood, string> = {
  [RiskLikelihood.veryLow]: 'Very Low',
  [RiskLikelihood.low]: 'Low',
  [RiskLikelihood.medium]: 'Medium',
  [RiskLikelihood.high]: 'High',
  [RiskLikelihood.veryHigh]: 'Very High',
};

export interface RiskMitigation {
  id: string;
  description: string;
  responsible: string;
  responsibleName: string;
  dueDate?: Date;
  completedDate?: Date;
  status: 'pending' | 'in_progress' | 'completed';
}

export interface Risk {
  id: string;

  // Source
  meetingId: string;
  meetingTitle: string;
  sourceTimestampStart?: number;
  sourceTimestampEnd?: number;
  sourceTextSnippet?: string;

  // Risk Content
  title: string;
  description: string;
  category: string; // 'technical', 'operational', 'financial', 'legal', etc.

  // Assessment
  severity: RiskSeverity;
  likelihood: RiskLikelihood;
  riskScore: number; // Calculated: severity * likelihood

  // Impact
  potentialImpact: string; // Description of what could happen
  affectedAreas: string[]; // Teams/products affected
  estimatedCost?: number; // Financial impact if risk realizes

  // Mitigation
  mitigationStrategy?: string;
  mitigationOwner?: string;
  mitigationOwnerId?: string;
  mitigationDueDate?: Date;
  mitigations: RiskMitigation[];

  // Status
  status: RiskStatus;
  approvalWorkflow: ApprovalStep[];

  // Monitoring
  lastReviewDate?: Date;
  nextReviewDate?: Date;
  reviewFrequency?: number; // days

  // Realization
  realizedAt?: Date;
  realizedNotes?: string;
  actualImpact?: string;
  actualCost?: number;

  // PHI Detection
  containsPHI: boolean;
  phiDataTypes: PHIDataType[];

  // AI Confidence
  aiConfidence: number;
  aiExtractionNotes?: string;

  // Metadata
  tags: string[];
  relatedDecisionIds: string[];
  relatedTaskIds: string[];
  customFields?: Record<string, unknown>;

  // Audit
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
}

export interface RiskAnalytics {
  totalRisks: number;
  byStatus: Record<RiskStatus, number>;
  bySeverity: Record<RiskSeverity, number>;
  byLikelihood: Record<RiskLikelihood, number>;
  avgRiskScore: number;
  highSeverityCount: number;
  realizedCount: number;
  mitigatedCount: number;
}
